import { FC, useState, useEffect, lazy, Suspense } from 'react';
import { DashboardLayout } from '@components/layout/DashboardLayout';
import { PropertyTable } from '@components/properties/PropertyTable';
import { PropertyFilterBar, ViewMode } from '@components/properties/PropertyFilterBar';
import { PropertyCard } from '@components/properties/PropertyCard';
import { PropertyFormWizard } from '@components/properties/PropertyFormWizard';
import { Modal } from '@components/feedback/Modal';
import { Button } from '@components/common/Button';
import { Property, PropertyStatus } from '@/types/index';
import { propertyService } from '@services/property.service';

const PropertyMap = lazy(() => import('@components/properties/PropertyMap').then(m => ({ default: m.PropertyMap })));

const STORAGE_KEY = 'immohelvet_first_selection_view_mode';

export const FirstSelection: FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem(STORAGE_KEY) as ViewMode | null) ?? 'cards'
  );
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | undefined>(
    undefined,
  );
  const [showOffMarket, setShowOffMarket] = useState(false);
  const [isReclassifying, setIsReclassifying] = useState(false);
  const [reclassifyResult, setReclassifyResult] = useState<{ total: number; updated: number } | null>(null);

  useEffect(() => {
    loadProperties();
  }, [showOffMarket]);

  const loadProperties = async () => {
    try {
      setIsLoading(true);
      const data = await propertyService.getAllFiltered(
        showOffMarket ? { isOffMarket: true } : undefined
      );
      setProperties(data.properties);
    } catch (error) {
      console.error('Erreur lors du chargement de la 1ère sélection:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProperty = () => {
    setSelectedProperty(undefined);
    setIsWizardOpen(true);
  };

  const handleEditProperty = async (property: Property) => {
    try {
      const freshProperty = await propertyService.getById(property.id);
      setSelectedProperty(freshProperty);
    } catch (error) {
      console.error('Erreur chargement propriété:', error);
      setSelectedProperty(property);
    }
    setIsWizardOpen(true);
  };

  const handleDeleteProperty = async (id: string) => {
    try {
      await propertyService.delete(id);
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de la propriété');
    }
  };

  const handleWizardSuccess = () => {
    setIsWizardOpen(false);
    setSelectedProperty(undefined);
    loadProperties();
  };

  const handleWizardCancel = () => {
    setIsWizardOpen(false);
    setSelectedProperty(undefined);
    loadProperties();
  };

  const handleRejectProperty = async (property: Property) => {
    try {
      await propertyService.update(property.id, { status: PropertyStatus.REJECTED });
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors du rejet:', error);
      alert('Erreur lors du rejet de la propriété');
    }
  };

  const handleRestoreProperty = async (property: Property) => {
    try {
      await propertyService.update(property.id, { status: PropertyStatus.FIRST_SELECTION });
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors de la restauration:', error);
      alert('Erreur lors de la restauration de la propriété');
    }
  };

  const handleReclassify = async () => {
    setIsReclassifying(true);
    setReclassifyResult(null);
    try {
      const result = await propertyService.reclassifyAll();
      await loadProperties();
      setReclassifyResult(result);
      setTimeout(() => setReclassifyResult(null), 8000);
    } catch (error) {
      console.error('Erreur lors du recalcul:', error);
    } finally {
      setIsReclassifying(false);
    }
  };

  const handlePromoteProperty = async (property: Property) => {
    try {
      const newStatus = property.status === PropertyStatus.OPPORTUNITY
        ? PropertyStatus.FIRST_SELECTION
        : PropertyStatus.OPPORTUNITY;
      await propertyService.update(property.id, { status: newStatus });
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors de la promotion:', error);
    }
  };

  return (
    <DashboardLayout title="1ère sélection">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
            1ère sélection
          </h1>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button onClick={handleReclassify} variant="secondary" disabled={isReclassifying}>
              {isReclassifying ? 'Calcul…' : '↺ Recalculer'}
            </Button>
            <Button onClick={handleAddProperty} variant="primary">
              + Ajouter
            </Button>
          </div>
        </div>

        {/* Résultat recalcul */}
        {reclassifyResult && (
          <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl text-sm">
            <span className="font-semibold text-emerald-800 dark:text-emerald-300">Scores recalculés</span>
            <span className="text-emerald-700 dark:text-emerald-400">
              {reclassifyResult.updated} annonce{reclassifyResult.updated > 1 ? 's' : ''} mise{reclassifyResult.updated > 1 ? 's' : ''} à jour sur {reclassifyResult.total}
            </span>
            <button onClick={() => setReclassifyResult(null)} className="ml-auto text-gray-400 hover:text-gray-600">✕</button>
          </div>
        )}

        {/* Filtres */}
        <PropertyFilterBar
          properties={properties}
          onFilterChange={setFilteredProperties}
          defaultStatuses={[PropertyStatus.FIRST_SELECTION]}
          showOffMarket={showOffMarket}
          onToggleOffMarket={() => setShowOffMarket(v => !v)}
          viewMode={viewMode}
          onViewModeChange={(m) => { setViewMode(m); localStorage.setItem(STORAGE_KEY, m); }}
        />

        {/* Vue tableau */}
        {viewMode === 'table' && (
          <PropertyTable
            properties={filteredProperties}
            onEdit={handleEditProperty}
            onDelete={handleDeleteProperty}
            onReject={handleRejectProperty}
            onRestore={handleRestoreProperty}
            isLoading={isLoading}
          />
        )}

        {/* Vue cards */}
        {viewMode === 'cards' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-32 bg-gray-100 dark:bg-gray-700 rounded-xl animate-pulse" />
                ))
              : filteredProperties.map(p => (
                  <PropertyCard
                    key={p.id}
                    property={p}
                    onEdit={handleEditProperty}
                    onReject={handleRejectProperty}
                    onRestore={handleRestoreProperty}
                    onPromote={handlePromoteProperty}
                  />
                ))
            }
          </div>
        )}

        {/* Vue carte */}
        {viewMode === 'map' && (
          <Suspense fallback={<div className="h-[600px] bg-gray-100 dark:bg-gray-700 rounded-xl animate-pulse" />}>
            <PropertyMap properties={filteredProperties} />
          </Suspense>
        )}

        {/* Modal Wizard */}
        <Modal
          isOpen={isWizardOpen}
          onClose={handleWizardCancel}
          title={
            selectedProperty ? 'Modifier la propriété' : 'Ajouter une propriété'
          }
          size="xl"
        >
          <PropertyFormWizard
            property={selectedProperty}
            onSuccess={handleWizardSuccess}
            onCancel={handleWizardCancel}
            onSave={loadProperties}
          />
        </Modal>
      </div>
    </DashboardLayout>
  );
};
